import { zodResolver } from '@hookform/resolvers/zod'
import { Banknote, CreditCard, QrCode } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { apiFetcher } from '@/data/fetcher'
import { SettingsRow } from '@/forms/components/SettingsRow'
import { useCountries } from '@/hooks/useCountries'
import { useFeatureCheck } from '@/hooks/useFeatureCheck'
import { Collection } from '@/interfaces/Collection'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { cn } from '@/lib/utils'
import { components, countryCode, operations } from '@invo.space/shared'

import {
  CashRegisterSettingsFormSchema,
  CashRegisterSettingsFormValues,
} from '@invo.space/shared'

export type CashRegisterSettingsResponse = operations['getCashRegisterSettings']['responses']['200']['content']['application/json']

export interface CashRegisterSettingsFormProps {
  settings?: CashRegisterSettingsResponse
}

export const CashRegisterSettingsForm = ({
  settings,
}: CashRegisterSettingsFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'cashRegister.settings.form',
  })
  const { t: tReceipts } = useTranslation()
  const { t: tg } = useTranslation(undefined, {
    keyPrefix: 'generic',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const { isEnabled: warehouseEnabled } = useFeatureCheck('warehouse')

  const { data: taxRates, isLoading: isLoadingTaxRates } = useSWR<
    Collection<components['schemas']['TaxRate']>
  >('/api/v2/tax-rates', apiFetcher)

  const { data: warehousesResponse } = useSWR<{ data?: components['schemas']['Warehouse'][] }>(
    warehouseEnabled ? '/api/v2/warehouses' : null,
    apiFetcher,
  )

  const warehouses = warehousesResponse?.data ?? []

  const countries = useCountries()


  const defaultValues: CashRegisterSettingsFormValues = {
    cashRegisterIdentifier: settings?.cashRegisterIdentifier || '',
    amountThenPrice: settings?.amountThenPrice ?? false,
    address: {
      address: settings?.address?.address || '',
      city: settings?.address?.city || '',
      zip: settings?.address?.zip || '',
      country: settings?.address?.country ? countryCode(settings.address.country) : 'CZ',
    },
    workplaceIdentifier: settings?.workplaceIdentifier || '',
    taxRates: settings?.taxRates?.map((tax) => tax.id.toString()) || [],
    defaultTaxRate: settings?.defaultTaxRate?.id.toString() || '',
    printEmail: settings?.showEmail ?? false,
    printPhone: settings?.showPhone ?? false,
    paymentTypes: settings?.paymentTypes ?? ['Card', 'Cash', 'Qr'],
    warehouseId: settings?.warehouseId ?? '',
  }

  const methods = useForm<CashRegisterSettingsFormValues>({
    resolver: zodResolver(CashRegisterSettingsFormSchema),
    defaultValues,
  })

  useEffect(() => {
    if (settings) {
      methods.reset({
        ...defaultValues,
        cashRegisterIdentifier: settings.cashRegisterIdentifier ?? '',
        amountThenPrice: settings.amountThenPrice ?? false,
        address: {
          address: settings.address?.address ?? '',
          city: settings.address?.city ?? '',
          zip: settings.address?.zip ?? '',
          country: settings.address?.country ? countryCode(settings.address.country) : 'CZ',
        },
        workplaceIdentifier: settings.workplaceIdentifier ?? '',
        taxRates: settings.taxRates?.map((tax) => tax.id.toString()) ?? [],
        defaultTaxRate: settings.defaultTaxRate?.id.toString() ?? '',
        printEmail: settings.showEmail ?? false,
        printPhone: settings.showPhone ?? false,
        paymentTypes: settings.paymentTypes ?? ['Card', 'Cash', 'Qr'],
        warehouseId: settings.warehouseId ?? '',
      })
    }
  }, [settings])

  const taxRatesValue = methods.watch('taxRates')
  const defaultTaxRateValue = methods.watch('defaultTaxRate')

  const onValid = async (values: CashRegisterSettingsFormValues) => {
    setIsSubmitting(true)
    try {
      await API.post('/api/v1/cash-register/settings', {
        cashRegisterIdentifier: values.cashRegisterIdentifier,
        amountThenPrice: values.amountThenPrice,
        address: values.address,
        workplaceId: values.workplaceIdentifier,
        taxRates: values.taxRates,
        defaultTaxRate: values.defaultTaxRate,
        showEmail: values.printEmail,
        showPhone: values.printPhone,
        paymentTypes: values.paymentTypes,
        warehouseId: values.warehouseId || null,
      })

      toast.success(t('success'))
    } catch (error: unknown) {
      console.error('Cash register settings form error:', error)
      const errorMessage = extractErrorMessage(error)
      toast.error(t('error'), {
        description: errorMessage || t('error.description'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onInvalid = (errors: FieldErrors<CashRegisterSettingsFormValues>) => {
    console.log('Form errors', errors, methods.getValues())
  }

  const isDirty = methods.formState.isDirty

  return (
    <FormProvider {...methods}>
      <Form {...methods}>
        <form onSubmit={methods.handleSubmit(onValid, onInvalid)} className="w-full">
          <SettingsRow title="Účtenka" description="Údaje, které se tisknou v hlavičce každé účtenky.">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={methods.control}
                name="cashRegisterIdentifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('cashRegisterIdentifier.label')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('cashRegisterIdentifier.placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={methods.control}
                name="workplaceIdentifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('workplaceIdentifier.label')}</FormLabel>
                    <FormControl>
                      <Input placeholder="Číslo z EET 2.0, např. 11" className="tabular" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <fieldset className="grid gap-3">
              <legend className="mb-2 text-sm font-medium">{t('address.title')}</legend>
              <FormField
                control={methods.control}
                name="address.address"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input aria-label="Ulice a číslo" placeholder={t('address.address.placeholder')} autoComplete="street-address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-3">
                <FormField
                  control={methods.control}
                  name="address.zip"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input aria-label={t('address.zip.label')} placeholder={t('address.zip.placeholder')} className="tabular" autoComplete="postal-code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={methods.control}
                  name="address.city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input aria-label={t('address.city.label')} placeholder={t('address.city.placeholder')} autoComplete="address-level2" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={methods.control}
                name="address.country"
                render={({ field }) => (
                  <FormItem>
                    <Select value={field.value ?? undefined} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger aria-label="Země" className="w-full">
                          <SelectValue placeholder={t('address.country.placeholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem value={country.code} key={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>

            <div className="grid gap-1">
              <p className="mb-1 text-sm font-medium">Na účtenku tisknout také</p>
              <SwitchField name="printEmail" label="E-mail" description="Kontaktní e-mail z nastavení účtu." />
              <SwitchField name="printPhone" label="Telefon" description="Kontaktní telefon z nastavení účtu." />
            </div>
          </SettingsRow>

          <SettingsRow title="Prodej" description="Jak se na pokladně zadávají položky a odkud se bere zásoba.">
            {warehouseEnabled && (
              <FormField
                control={methods.control}
                name="warehouseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('warehouseId.label')}</FormLabel>
                    <Select value={field.value || undefined} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full sm:w-80">
                          <SelectValue placeholder={t('warehouseId.placeholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {warehouses
                          .filter((warehouse) => warehouse.isActive !== false)
                          .map((warehouse) => (
                            <SelectItem key={warehouse.id} value={warehouse.id!}>
                              {warehouse.name} ({warehouse.code})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('warehouseId.description')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={methods.control}
              name="amountThenPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pořadí zadávání na kalkulačce</FormLabel>
                  <RadioGroup
                    value={field.value ? 'amount' : 'price'}
                    onValueChange={(v) => field.onChange(v === 'amount')}
                    className="grid gap-2 sm:grid-cols-2"
                  >
                    {[
                      { value: 'price', title: 'Cena, pak množství', example: '89 × 3' },
                      { value: 'amount', title: 'Množství, pak cena', example: '3 × 89' },
                    ].map((o) => (
                      <label
                        key={o.value}
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-lg bg-muted/60 px-4 py-3 transition-colors hover:bg-muted has-[[data-state=checked]]:bg-brand-soft',
                        )}
                      >
                        <FormControl>
                          <RadioGroupItem value={o.value} className="mt-0.5" />
                        </FormControl>
                        <span className="grid gap-0.5">
                          <span className="text-sm font-medium">{o.title}</span>
                          <span className="text-xs text-muted-foreground tabular">Např. {o.example}</span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                  <FormMessage />
                </FormItem>
              )}
            />
          </SettingsRow>

          <SettingsRow title={t('paymentTypes.label')} description="Tlačítka plateb, která pokladna nabídne. Aspoň jeden musí zůstat zapnutý.">
            <FormField
              control={methods.control}
              name="paymentTypes"
              render={({ field }) => (
                <FormItem>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[
                      { value: 'Cash' as const, icon: Banknote, hint: 'Platba v hotovosti.' },
                      { value: 'Card' as const, icon: CreditCard, hint: 'Na vlastním terminálu, bez propojení.' },
                      { value: 'Qr' as const, icon: QrCode, hint: 'QR platba na obrazovce.' },
                    ].map(({ value, icon: Icon, hint }) => {
                      const checked = field.value.includes(value)
                      return (
                        <label
                          key={value}
                          className={cn(
                            'relative flex cursor-pointer flex-col gap-3 rounded-lg px-4 py-3.5 transition-colors',
                            checked ? 'bg-brand-soft' : 'bg-muted/60 hover:bg-muted',
                          )}
                        >
                          <span className="flex items-center justify-between">
                            <span className={cn('grid size-8 place-items-center rounded-md', checked ? 'bg-background text-brand-strong' : 'bg-background text-muted-foreground')}>
                              <Icon className="size-4" />
                            </span>
                            <FormControl>
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(next) =>
                                  field.onChange(next ? [...field.value, value] : field.value.filter((v) => v !== value))
                                }
                              />
                            </FormControl>
                          </span>
                          <span className="grid gap-0.5">
                            <span className="text-sm font-medium">{tReceipts(`cashRegister.receipts.paymentType.${value}`)}</span>
                            <span className="text-xs text-muted-foreground">{hint}</span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </SettingsRow>

          <SettingsRow title={t('taxRates.title')} description="Sazby, ze kterých se na pokladně vybírá. Výchozí se předvyplní u vlastní položky.">
            {isLoadingTaxRates && <Loading />}
            {!isLoadingTaxRates && taxRates && (
              <FormField
                control={methods.control}
                name="taxRates"
                render={({ field }) => (
                  <FormItem>
                    <FormField
                      control={methods.control}
                      name="defaultTaxRate"
                      render={({ field: defaultField }) => (
                        <RadioGroup value={defaultField.value.toString() ?? ''} onValueChange={defaultField.onChange} className="gap-0">
                          <div className="grid grid-cols-[minmax(0,1fr)_88px_88px] items-center px-1 pb-2 text-[11px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                            <span>Sazba</span>
                            <span className="text-center">Povolená</span>
                            <span className="text-center">Výchozí</span>
                          </div>
                          {taxRates.data.map((tax) => {
                            const taxId = tax.id.toString()
                            const isChecked = taxRatesValue.includes(taxId)
                            const isDefault = defaultTaxRateValue === taxId
                            return (
                              <div
                                key={taxId}
                                className="grid min-h-11 grid-cols-[minmax(0,1fr)_88px_88px] items-center rounded-md px-1 hover:bg-muted/60"
                              >
                                <label htmlFor={`tax-rate-${taxId}`} className="flex cursor-pointer items-baseline gap-2 text-sm">
                                  <span className="w-10 font-medium tabular">{tax.percentage} %</span>
                                  {tax.name && <span className="text-muted-foreground">{tax.name}</span>}
                                </label>
                                <span className="flex justify-center">
                                  <Switch
                                    id={`tax-rate-${taxId}`}
                                    checked={isChecked}
                                    aria-label={`Povolit sazbu ${tax.percentage} %`}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked ? [...taxRatesValue, taxId] : taxRatesValue.filter((id) => id !== taxId))
                                      if (!checked && isDefault) defaultField.onChange('')
                                    }}
                                  />
                                </span>
                                <span className="flex justify-center">
                                  <RadioGroupItem
                                    value={taxId}
                                    id={`default-tax-rate-${taxId}`}
                                    aria-label={`Výchozí sazba ${tax.percentage} %`}
                                    disabled={!isChecked}
                                    onBlur={defaultField.onBlur}
                                  />
                                </span>
                              </div>
                            )
                          })}
                        </RadioGroup>
                      )}
                    />
                    <FormMessage />
                    <FormField control={methods.control} name="defaultTaxRate" render={() => <FormMessage />} />
                  </FormItem>
                )}
              />
            )}
          </SettingsRow>

          <div className="sticky bottom-0 z-10 -mx-1 mt-2 flex items-center justify-end gap-3 bg-background/95 px-1 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            {isDirty && <span className="mr-auto text-sm text-muted-foreground">Máte neuložené změny</span>}
            {isDirty && (
              <Button type="button" variant="ghost" onClick={() => methods.reset()}>
                Zahodit
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? tg('saving') : tg('button.save')}
            </Button>
          </div>
        </form>
      </Form>
    </FormProvider>
  )
}

const SwitchField = ({ name, label, description }: { name: 'printEmail' | 'printPhone'; label: string; description: string }) => (
  <FormField
    name={name}
    render={({ field }) => (
      <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md py-1.5">
        <div className="grid gap-0.5">
          <FormLabel className="font-normal">{label}</FormLabel>
          <FormDescription className="text-xs">{description}</FormDescription>
        </div>
        <FormControl>
          <Switch checked={field.value} onCheckedChange={field.onChange} />
        </FormControl>
      </FormItem>
    )}
  />
)
